import {
  useDashboardPageContext,
  useFormatZoned,
  useSelectedDay,
} from "utils/customHooks/genericHooks";
import { Button } from "@mui/material";
import { CalendarTodayOutlined } from "@mui/icons-material";
import { goToDay } from "providers/GlobalStateProvider";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useEffect, useState, useTransition } from "react";
import { useDispatch } from "react-redux";
import {
  MIN_DATE,
  ONE_DAY_OF_MS,
  getMidnightTodayMs,
  getFullDayRangeTitle,
} from "utils/timeUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { DatePickerCalendar } from "./DatePickerCalendar";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useSelectedRange } from "../useSelectedRange";
import styled from "styled-components/macro";
import { BREAKPOINT_MOBILE } from "constants/constants";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useDateMetadataDisplay } from "components/FlightTrends/DateRange/useDateMetadataDisplay";
import { HtmlTooltip } from "components/Dashboard/GroupMetrics/HtmlTooltip";
import { TimeOffsetExplainer } from "./TimeOffsetExplainer";

export function DatePickerButton() {
  const formatZoned = useFormatZoned();
  const { isFullDaySelected } = useDashboardPageContext();
  const selectedRange = useSelectedRange();
  const [open, setOpen] = useState(false);
  const { innerHeight } = useWindowSize();
  // when the menu opens, if the calendar would be cut off by .cardsWrapper's overflow: hidden, add class overflowVisible to it
  useEffect(() => {
    const cardsWrapper = document.querySelector(".cardsWrapper");
    if (open && innerHeight < 800) {
      cardsWrapper?.classList.add("overflowVisible");
    } else if (cardsWrapper?.classList.contains("overflowVisible")) {
      cardsWrapper?.classList.remove("overflowVisible");
    }
  }, [open, innerHeight]);

  const dispatch = useDispatch();
  const { dayStartMsOffset } = useSettings();
  const [, startTransition] = useTransition();
  function onChange(nextDate: Date) {
    const nextSelectedRange = {
      ge: nextDate.getTime(),
      lt: nextDate.getTime() + ONE_DAY_OF_MS,
    };
    startTransition(() => {
      dispatch(
        goToDay({
          newSelectedDay: {
            range: nextSelectedRange,
            shift: {
              name: getFullDayRangeTitle(
                nextSelectedRange.ge,
                nextSelectedRange.lt,
                formatZoned
              ),
            },
          },
          dayStartMsOffset,
          timeZoneMsOffset,
          shouldGoToFirstShiftInDay: !isFullDaySelected,
          shouldGoToCurrentShift: false,
          formatZoned,
        })
      );
      setOpen(false);
    });
  }
  const timeZoneMsOffset = useTimeZoneMsOffset();

  const now = Date.now();
  const midnightTodayMs = getMidnightTodayMs(now, timeZoneMsOffset);

  const minDate = new Date(MIN_DATE);
  const maxDate = new Date(midnightTodayMs);

  const reactDatePickerGe = new Date(selectedRange.ge + timeZoneMsOffset);
  const selectedDay = useSelectedDay();

  const isToday =
    selectedDay.range.ge + dayStartMsOffset <= midnightTodayMs &&
    midnightTodayMs < selectedDay.range.lt + dayStartMsOffset;
  const nowYesterday = now - ONE_DAY_OF_MS;
  const midnightYesterday = new Date(nowYesterday).setHours(0, 0, 0, 0);
  const isYesterday =
    midnightYesterday <= selectedRange.ge + dayStartMsOffset &&
    selectedRange.ge + dayStartMsOffset < midnightTodayMs;
  const { translate } = useFlightTranslation();
  const dateFormatted = selectedRange.ge
    ? formatZoned(
        selectedRange.ge + dayStartMsOffset + timeZoneMsOffset,
        "EEEE MMM d, yyyy"
      )
    : "";

  const { dayStartMsOffsetDisplay, timezoneDisplay } = useDateMetadataDisplay();

  return selectedRange.ge ? (
    <DatePickerButtonContent
      {...{
        isToday,
        isYesterday,
        setOpen,
        open,
        translate,
        dayStartMsOffsetDisplay,
        timezoneDisplay,
        dateFormatted,
        onChange,
        reactDatePickerGe,
        minDate,
        maxDate,
      }}
    />
  ) : null;
}

export function DatePickerButtonContent({
  isToday,
  isYesterday,
  setOpen,
  open,
  translate,
  dayStartMsOffsetDisplay,
  timezoneDisplay,
  dateFormatted,
  onChange,
  reactDatePickerGe,
  minDate,
  maxDate,
}: {
  isToday: boolean;
  isYesterday: boolean;
  setOpen: (open: boolean) => void;
  open: boolean;
  translate: (key: string) => string;
  dayStartMsOffsetDisplay: string;
  timezoneDisplay: string;
  dateFormatted: string;
  onChange: (nextDate: Date) => void;
  reactDatePickerGe: Date;
  minDate: Date;
  maxDate: Date;
}) {
  return (
    <DatePickerButtonStyles
      className={`btnDatePickerContainer${isToday ? " isToday" : ""}${
        isYesterday ? " isYesterday" : ""
      }`}
    >
      <Button
        className="btnDatePicker"
        onClick={() => {
          const nextOpen = !open;

          if (nextOpen) {
            // modify z-index of timeline while calendar is open so tooltip doesn't overlap on top of picker
            const timeline = document.getElementById("timelineStyles");
            if (timeline) {
              timeline.classList.remove("tooltipOpen");
            }
          }

          setOpen(nextOpen);
        }}
      >
        {isToday || isYesterday ? (
          <span className="btnDatePickerText">
            {"("}
            {translate(isToday ? "Today" : "Yesterday")}
            {")"}
          </span>
        ) : null}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0 }}>
          <HtmlTooltip
            openOnMouseover={true}
            allowPropagation={true}
            title={
              dayStartMsOffsetDisplay || timezoneDisplay ? (
                <TimeOffsetExplainer showTimeZoneInfo={true} />
              ) : (
                ""
              )
            }
          >
            <div className="offsetAndTimezoneIndicator">
              {dayStartMsOffsetDisplay}
              {dayStartMsOffsetDisplay && timezoneDisplay ? ", " : ""}
              {timezoneDisplay}
            </div>
          </HtmlTooltip>
        </div>
        <ErrorBoundary id="DatePickerButton_date">
          <div className="date date-display">{dateFormatted}</div>
        </ErrorBoundary>
        <CalendarTodayOutlined />
      </Button>
      <div
        className="dateRangePickerContainer"
        onClick={e => e.stopPropagation()}
      >
        <ErrorBoundary fallback={null} id="DatePickerButton_calendar">
          {open ? (
            <DatePickerCalendar
              open={open}
              setOpen={setOpen}
              onChange={onChange}
              reactDatePickerGe={reactDatePickerGe}
              minDate={minDate}
              maxDate={maxDate}
            />
          ) : null}
        </ErrorBoundary>
      </div>
    </DatePickerButtonStyles>
  );
}
const DatePickerButtonStyles = styled.div`
  .react-date-picker__calendar {
    margin-top: -26px;
    @media (max-width: ${BREAKPOINT_MOBILE}px) {
      margin-left: -44px;
    }
  }
  .react-date-picker__calendar--closed {
    display: none;
  }
`;
